import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageLoader } from '../components/Feedback';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { AppHeader } from './TopBar';
import { SideNav } from './SideNav';
// Headings in IBM Plex Serif, as on the website and its app mock-ups (2026-11-02).
import '@fontsource/ibm-plex-serif/latin-500.css';
import '@fontsource/ibm-plex-serif/latin-600.css';
import { SearchPalette } from './SearchPalette';
import { MOBILE_TABS } from './nav';
import { QuarterProvider } from './quarter';
import { CompanyProvider } from './company';
import { SessionBanners } from './SessionBanners';

/**
 * Logged-in shell: top header with three sections, the current-company switcher, bell and avatar menu; on wide screens
 * the side navigation of the website's app mock-up (2026-11-02); on phones a bottom tab bar (Dit kwartaal · Documenten ·
 * Historie · Account).
 * Providers: QuarterProvider (filing quarter) and CompanyProvider (useCompany) wrap every page.
 */
export function Shell() {
  const { t } = useI18n();
  const location = useLocation();
  const [search, setSearch] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // ⌘K / Ctrl K opens search everywhere (§9.7).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Move focus to main for screen readers on navigation.
  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // The back office is a workspace of its own (2026-11-03, J): full width, without the app's side bar and tab bar.
  const ops = location.pathname === '/ops' || location.pathname.startsWith('/ops/');

  return (
    <QuarterProvider>
      <CompanyProvider>
        <a href="#main" className="skip-link">
          {t('nav.skip')}
        </a>
        <div className={ops ? 'shell shell--ops' : 'shell'}>
          <AppHeader onOpenSearch={() => setSearch(true)} mode={ops ? 'ops' : 'app'} />
          <div className="shell__body">
            {!ops && <SideNav />}
            <main id="main" ref={mainRef} className={ops ? 'main main--full' : 'main'} tabIndex={-1}>
              <SessionBanners />
              {/* Per-route boundary (DC-01): a crashing page keeps the header usable. */}
              <ErrorBoundary resetKey={location.pathname}>
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </main>
          </div>
          {!ops && <MobileTabBar />}
        </div>
        <SearchPalette open={search} onClose={() => setSearch(false)} />
      </CompanyProvider>
    </QuarterProvider>
  );
}

/** Phones: four tabs. Uploading lives on the Documenten page (phase 2), not in the tab bar. */
function MobileTabBar() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  return (
    <nav className="tabbar" aria-label={t('nav.mobile')}>
      {MOBILE_TABS.map((item) => {
        const Icon = item.icon;
        return (
          <Link key={item.to} to={item.to} className="tabbar__item" aria-current={item.match(pathname) ? 'page' : undefined}>
            <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
            {t(item.label)}
          </Link>
        );
      })}
    </nav>
  );
}
